export enum ClientTagKey {
    READY_TO_COMMIT = "ready_to_commit",
    LOW_BARRIER_NOT_BOOKING = "low_barrier_not_booking",
    ON_THE_FENCE = "on_the_fence",
    NOT_YET = "not_yet",
    STOPPED_RESPONDING = "stopped_responding",
    WAITLIST = "waitlist",
    INCOMPLETE_INQUIRY = "incomplete_inquiry",
    INSURANCE_TIER_1 = "insurance_tier_1",
    INSURANCE_TIER_2 = "insurance_tier_2",
    INSURANCE_TIER_3 = "insurance_tier_3",
}

export type ClientTagKind = "manual" | "automatic";
export type ClientTagOrigin = "mantle" | "intake";
export type ClientTagAction = "added" | "removed";

export interface ClientTagUser {
    email: string;
    name: string;
}

export interface ClientTagState {
    active: boolean;
    kind: ClientTagKind;
    origin_system: ClientTagOrigin;
    added_by?: ClientTagUser;
    added_at?: string;
    revision: string;
}

export type ClientTags = Partial<Record<ClientTagKey, ClientTagState>>;

export const CLIENT_TAG_AUTOMATION_USER: ClientTagUser = {
    email: "system@intake.invalid",
    name: "Intake automation",
};

export const INSURANCE_TIER_TAG_KEYS: ClientTagKey[] = [
    ClientTagKey.INSURANCE_TIER_1,
    ClientTagKey.INSURANCE_TIER_2,
    ClientTagKey.INSURANCE_TIER_3,
];

export function manualInsuranceTier(
    tags: ClientTags = {}
): ClientTagKey | undefined {
    return INSURANCE_TIER_TAG_KEYS.filter(
        (key) => tags[key]?.active && tags[key]?.kind === "manual"
    ).sort((left, right) =>
        tags[right]!.revision.localeCompare(tags[left]!.revision)
    )[0];
}

export interface ClientTagActivity {
    client_tag_activity_id: string;
    intake_client_id: string;
    tag_key: ClientTagKey;
    kind: ClientTagKind;
    action: ClientTagAction;
    origin_system: ClientTagOrigin;
    author: ClientTagUser;
    created_at: string;
}

export interface ClientTagDefinition {
    key: ClientTagKey;
    label: string;
    description: string;
}

export const CLIENT_TAGS: ClientTagDefinition[] = [
    {
        key: ClientTagKey.READY_TO_COMMIT,
        label: "Ready to Commit",
        description: "The person has booked or is ready to start care.",
    },
    {
        key: ClientTagKey.LOW_BARRIER_NOT_BOOKING,
        label: "Low Barrier, Not Booking",
        description: "The person has a route to start care but has not booked.",
    },
    {
        key: ClientTagKey.ON_THE_FENCE,
        label: "On the Fence",
        description:
            "The person is interested in care but is still deciding whether or how to start.",
    },
    {
        key: ClientTagKey.NOT_YET,
        label: "Not Yet",
        description:
            "The person is not proceeding now but may benefit from a relevant future recheck.",
    },
    {
        key: ClientTagKey.STOPPED_RESPONDING,
        label: "Stopped Responding",
        description:
            "The person completed an inquiry but has stopped responding to follow-up.",
    },
    {
        key: ClientTagKey.WAITLIST,
        label: "Waitlist",
        description:
            "The person is waiting for a suitable therapist or available slot.",
    },
    {
        key: ClientTagKey.INCOMPLETE_INQUIRY,
        label: "Incomplete Inquiry",
        description: "The person started an inquiry but did not complete it.",
    },
    {
        key: ClientTagKey.INSURANCE_TIER_1,
        label: "Insurance Tier 1",
        description:
            "NYSHIP/Carelon or NYCE PPO, as confirmed by a benefits review.",
    },
    {
        key: ClientTagKey.INSURANCE_TIER_2,
        label: "Insurance Tier 2",
        description:
            "Other plans with a deductible under $3,000 or reimbursement of at least " +
            "90% UCR or 300% Medicare, as confirmed by a benefits review.",
    },
    {
        key: ClientTagKey.INSURANCE_TIER_3,
        label: "Insurance Tier 3",
        description:
            "High-deductible and other TBD plans with a deductible of $3,000 or more, " +
            "as confirmed by a benefits review.",
    },
];

export function canManageClientTags(acl?: {
    intake?: boolean;
    super_admin?: boolean;
    blocked?: boolean;
}): boolean {
    return Boolean(acl && !acl.blocked && (acl.intake || acl.super_admin));
}

export function isClientTagKey(value: unknown): value is ClientTagKey {
    return CLIENT_TAGS.some(({ key }) => key === value);
}

export function activeClientTagKeys(tags: ClientTags = {}): ClientTagKey[] {
    return CLIENT_TAGS.filter(({ key }) => tags[key]?.active).map(({ key }) => key);
}

export function mergeClientTags(...maps: Array<ClientTags | undefined>): ClientTags {
    const merged: ClientTags = {};
    for (const tags of maps) {
        for (const { key } of CLIENT_TAGS) {
            const incoming = tags?.[key];
            if (
                incoming &&
                (!merged[key] || incoming.revision > merged[key]!.revision)
            ) {
                merged[key] = incoming;
            }
        }
    }
    return merged;
}

export interface ClientTagsRequest {
    action: "add" | "remove" | "history";
    clientId: string;
    tagKey?: ClientTagKey;
    afterActivityId?: string | null;
}

export interface ClientTagHistoryPage {
    activities: ClientTagActivity[];
    nextCursor: string | null;
}
